import { snapshot } from '@/messages'
import {
  useLocalStream,
  useBlob,
  useS3Service,
  useRepository,
  useSound
} from '@/composables'
import {
  useAppStore,
  useUserStore,
  useSnapshotStore
} from '@/stores'
import { sleep } from '@/utils'
import { useTranslation } from 'i18next-vue'
import { mediator, messenger } from '@communication'

export default class SnapshotProcedure {
  constructor() {
    const { t } = useTranslation()
    const { playSound } = useSound(['camera'])
    this.name = 'snapshotProcedure'
    this.listener = null
    this.app = useAppStore()
    this.user = useUserStore()
    this.store = useSnapshotStore()
    this.t = t
    this.playSound = playSound
    this.snapshotReject = null
    // JPEG/PNG image this user dropped or uploaded that the board shows now, OCR reads it as is
    this.uploadedImage = null
  }

  install = () => {
    messenger.subscribe(this.name, 'strategy:data', this.handler)
  }

  uninstall = () => {
    messenger.unsubscribe(this.name)
  }

  handler = async (event, channel) => {
    const { downloadSnapshot, getTemporarySnapshotUploadLinks, uploadToS3 } = useS3Service()
    const { type } = event
    switch (type) {
      case 'snapshot:take': {
        if (this.user.isClient) {
          this.playSound('camera')
          this.app.loading(this.t('loader.takingSnapshot'))
          const { blobToBase64 } = useBlob()
          try {
            // No async executor here: a throw inside an async executor never settles
            // the constructed promise, so a failed capture would hang the loader forever
            const { imageData, thumbnailData } = await new Promise((resolve, reject) => {
              this.snapshotReject = reject
              const { getSnapshot } = useLocalStream()
              getSnapshot()
                .then(({ data, thumbnailData }) => resolve({ imageData: data, thumbnailData }))
                .catch(reject)
            });
            const image = imageData instanceof window.Blob ? await blobToBase64(imageData) : imageData
            const thumbnail = thumbnailData instanceof window.Blob ? await blobToBase64(thumbnailData) : thumbnailData
            if (this.app.state.matches('app.recording.on')) {
              this.saveToBoard(image)
            }
            this.app.transition('draw')
            this.listener({ image, isThumbnail: false })
            const { uploadLink, thumbnailUploadLink } = await getTemporarySnapshotUploadLinks()
            await uploadToS3(thumbnailUploadLink, thumbnail)
            this.send(snapshot.downloadThumbnailMessage())
            await uploadToS3(uploadLink, image)
            this.send(snapshot.downloadMessage())
          } catch (error) {
            console.error(`[snapshot:take] ${error}`)
            this.app.transition('LEAVE_DRAW')
          }
          this.app.loaded()
        }
        break
      }
      case 'snapshot:dismiss': {
        this.app.transition('LEAVE_DRAW')
        break
      }
      case 'snapshot:download-thumbnail': {
        const image = await downloadSnapshot(true);
        this.uploadedImage = null
        this.app.transition('draw')
        this.listener({ isThumbnail: true, image })
        this.app.loading(this.t('loader.transferringSnapshot', {
          progress: 100
        }))
        this.app.loaded()
        if (!this.user.isAssistant) {
          this.playSound('camera')
        }
        break
      }
      case 'snapshot:download': {
        const image = await downloadSnapshot();
        this.listener({ isThumbnail: false, image })
        break
      }
      case 'snapshot:cancel': {
        this.snapshotReject?.(new Error("Cancel"));
        this.app.transition('LEAVE_DRAW')
        this.app.loaded()
        this.snapshotReject = null
        break
      }
    }
  }

  cancelSnapshot = () => {
    this.send(snapshot.cancelSnapshotMessage())
    if (this.user.isClient) {
      this.snapshotReject?.(new Error("Cancel"));
      this.app.loaded()
      this.snapshotReject = null
    }
    this.dismiss('snapshot')
  }

  take = () => {
    const { sendWsEvent } = useRepository()
    this.playSound('camera')
    this.app.loading(this.t('loader.takingSnapshot'))
    this.store.setTakePictureTouch({
      isUpload: true,
      isSaveDone: true,
    })
    this.send(snapshot.takeMessage())
    sendWsEvent(snapshot.takeMessage())
  }

  dismiss = async (type) => {
    const { deleteSnapshot } = useS3Service()
    const { sendWsEvent } = useRepository()
    if (type) {
      this.app.loading(type === 'snapshot' ? this.t('loader.savingSnapshot') : this.t('loader.savingUploadedImage'))
    }
    this.send(snapshot.dismissMessage())
    this.uploadedImage = null
    this.store.setTakePictureTouch({
      isSaveDone: false,
    })
    sendWsEvent(snapshot.dismissMessage())
    await deleteSnapshot()
    this.app.transition('LEAVE_DRAW')
    await sleep(1000)
    this.app.loaded()
  }

  generateThumbnail = async (data) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, img.width, img.height)
        const thumbnailData = canvas.toDataURL('image/jpeg', 0.1)
        resolve(thumbnailData)
      }
      img.onerror = reject
      img.src = data
    })
  }

  drop = async (data) => {
    const { getTemporarySnapshotUploadLinks, uploadToS3 } = useS3Service()
    // Same as take(): the image is not saved here, OCR saves what it reads on demand (MeetingBoard recognizeOcr)
    this.store.setTakePictureTouch({
      isUpload: true,
      isSaveDone: true,
    })
    // Generate thumbnail from base64 image data
    const thumbnail = await this.generateThumbnail(data)
    const { uploadLink, thumbnailUploadLink } = await getTemporarySnapshotUploadLinks()
    await uploadToS3(thumbnailUploadLink, thumbnail)
    this.send(snapshot.downloadThumbnailMessage())
    this.app.transition('draw')
    this.app.loaded()
    // Textract reads JPEG and PNG, OCR of other formats falls back to the board export
    this.uploadedImage = /^data:image\/(jpeg|png);/.test(data) ? data : null
    this.listener({ isThumbnail: false, image: data })
    await uploadToS3(uploadLink, data)
    this.send(snapshot.downloadMessage())
    if (this.app.state.matches('app.recording.on')) {
      this.saveToBoard(data)
    }
  }

  send = (message) => {
    mediator.broadcast(message)
  }

  save = async (data) => {
    try {
      const { saveSnapshot } = useS3Service()
      const { sendWsEvent } = useRepository()
      const { key, bucket } = await saveSnapshot(data)
      await this.store.setKey(key)
      await this.store.setTakePictureTouch({
        isSaveDone: true,
      })
      sendWsEvent(snapshot.saveMessage({ key, bucket }))
    } catch (error) {
      console.error(error)
    }
  }

  saveToBoard = async (data) => {
    // Called fire-and-forget, so it must never reject
    try {
      const { saveSnapshot } = useS3Service()
      const { sendWsEvent } = useRepository()
      const { key, bucket } = await saveSnapshot(data)
      sendWsEvent(snapshot.boardMessage({ key, bucket }))
    } catch (error) {
      console.error('[snapshot:saveToBoard]', error)
    }
  }

  // Saves the dropped or uploaded image as is for OCR. No event is sent, so it is not added to the media
  // library. Rejects on failure, so OCR never reads the key of a previous image.
  saveUploadedImage = async () => {
    const { saveSnapshot } = useS3Service()
    const { key } = await saveSnapshot(this.uploadedImage)
    this.store.setKey(key)
  }

  addListener = (listener) => {
    this.listener = listener
  }
}
